// Sub-Navigation Controller
// Handles the sub-nav section display based on the active section

export class SubNavController extends HTMLElement {
    constructor() {
        super();
        // Track hover state
        this.isHoveringMainNav = false;
        this.isHoveringSubNav = false;
        this.hoverTimeout = null;
        this.closeDelay = 500; // 500ms delay before closing

        if (!this.mainNav || !this.subNav) return;
    }

    connectedCallback() {
        this.init();
    }

    init() {
        this.initClassReferences();

        // Determine active section from URL
        this.detectActiveSection();

        // Set up hover behavior for preview
        this.setupHoverPreview();

        // Set up click behavior for all nav items
        this.setupClickBehavior();
    }

    initClassReferences() {
        this.siteHeader = this.querySelector('.site-header');
        this.mainNav = this.querySelector('.main-nav');
        this.subNav = this.querySelector('.sub-nav');
        this.navItems = this.querySelectorAll('.nav-item[data-section]');
        this.subNavSections = this.querySelectorAll('.sub-nav-section[data-section]');

    }

    detectActiveSection() {
        const path = window.location.pathname;
        let activeSection = null;

        // Determine section based on URL path
        if (path === '/' || path === '') {
            activeSection = 'home';
        } else if (path.includes('/introduction')) {
            activeSection = 'introduction';
        } else if (path.includes('/axiom')) {
            activeSection = 'axioms';
        } else if (path.includes('/atomic') ||
                   path.includes('/qm-deception') ||
                   path.includes('/topic/double-slit') ||
                   path.includes('/topic/quantum-entanglement') ||
                   path.includes('/topic/photoelectric-effect') ||
                   path.includes('/topic/maxwells-equations') ||
                   path.includes('/topic/hydrogen-spectral') ||
                   path.includes('/topic/planetary-resonance') ||
                   path.includes('/topic/helium-properties') ||
                   path.includes('/topic/fundamental-constants') ||
                   path.includes('/topic/em-pressure-waves')) {
            activeSection = 'atomic';
        } else if (path.includes('/cosmic') ||
                   path.includes('/big-bang-deception')) {
            activeSection = 'cosmic';
        } else if (path.includes('/general')) {
            activeSection = 'general';
        } else if (path.includes('/reference')) {
            activeSection = 'reference';
        }

        this.currentSection = activeSection;
        this.setActiveSection(activeSection);
    }

    setActiveSection(section) {
        // Clear all active states
        this.navItems.forEach(item => item.classList.remove('active'));
        this.subNavSections.forEach(sec => sec.classList.remove('active'));

        if (!section) {
            // No active section - remove subnav-active class
            this.updateSubNavActiveState(false);
            return;
        }

        // Set active nav item
        const activeNavItem = document.querySelector(`.nav-item[data-section="${section}"]`);
        if (activeNavItem) {
            activeNavItem.classList.add('active');
        }

        // Check if this section has a sub-nav
        const activeSubNavSection = document.querySelector(`.sub-nav-section[data-section="${section}"]`);
        if (activeSubNavSection) {
            activeSubNavSection.classList.add('active');
            // Sub-nav is active - add subnav-active class to dim main nav
            this.updateSubNavActiveState(true);
        } else {
            // No sub-nav for this section - remove subnav-active class
            this.updateSubNavActiveState(false);
        }

        // Highlight active axiom if on axiom page
        if (section === 'axioms') {
            this.highlightActiveAxiom();
        }
    }

    updateSubNavActiveState(isActive) {
        if (!this.siteHeader) return;

        if (isActive) {
            this.siteHeader.classList.add('subnav-active');
        } else {
            this.siteHeader.classList.remove('subnav-active');
        }
    }

    highlightActiveAxiom() {
        const path = window.location.pathname;
        const match = path.match(/\/axiom\/(\d+)/);
        if (match) {
            const axiomNumber = match[1];
            const axiomLinks = document.querySelectorAll('.axiom-links a');
            axiomLinks.forEach(link => {
                link.classList.remove('active');
                const numberSpan = link.querySelector('.axiom-number');
                if (numberSpan && numberSpan.textContent.trim() === axiomNumber) {
                    link.classList.add('active');
                }
            });
        }
    }

    clearHoverTimeout() {
        if (this.hoverTimeout) {
            clearTimeout(this.hoverTimeout);
            this.hoverTimeout = null;
        }
    }

    scheduleRevert() {
        this.clearHoverTimeout();
        this.hoverTimeout = setTimeout(() => {
            // Only revert if not hovering over anything
            if (!this.isHoveringMainNav && !this.isHoveringSubNav) {
                this.setActiveSection(this.currentSection);
            }
        }, this.closeDelay);
    }

    setupClickBehavior() {
        // Handle clicks on nav items that don't have subnavs
        // This provides immediate visual feedback before page navigation
        this.navItems.forEach(item => {
            const link = item.querySelector('a');
            if (link && !item.classList.contains('nav-dropdown')) {
                link.addEventListener('click', () => {
                    const section = item.dataset.section;

                    // Clear any hover state
                    this.clearHoverTimeout();
                    this.isHoveringMainNav = false;
                    this.isHoveringSubNav = false;

                    // Immediately show this section's sub-nav (if any) with transition
                    this.showSubNavSection(section);
                });
            }
        });
    }

    setupHoverPreview() {
        // Handle main nav item hover
        this.navItems.forEach(item => {
            if (!item.classList.contains('has-subnav')) return;

            item.addEventListener('mouseenter', () => {
                this.isHoveringMainNav = true;
                this.clearHoverTimeout();

                const section = item.dataset.section;
                this.showSubNavSection(section);
            });

            item.addEventListener('mouseleave', () => {
                this.isHoveringMainNav = false;
                this.scheduleRevert();
            });
        });

        // Handle sub-nav hover (entire sub-nav bar)
        this.subNav.addEventListener('mouseenter', () => {
            this.isHoveringSubNav = true;
            this.clearHoverTimeout();
        });

        this.subNav.addEventListener('mouseleave', () => {
            this.isHoveringSubNav = false;
            this.scheduleRevert();
        });
    }

    showSubNavSection(section) {
        // Hide all sub-nav sections
        this.subNavSections.forEach(sec => sec.classList.remove('active'));

        // Show the specified section
        const targetSection = document.querySelector(`.sub-nav-section[data-section="${section}"]`);
        if (targetSection) {
            targetSection.classList.add('active');
            // Sub-nav is showing - dim main nav
            this.updateSubNavActiveState(true);
        } else {
            // No sub-nav for this section - restore main nav brightness
            this.updateSubNavActiveState(false);
        }

        // Update nav item active state
        this.navItems.forEach(item => {
            item.classList.toggle('active', item.dataset.section === section);
        });
    }
}

customElements.define('subnav-controller', SubNavController);